import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Wordmark } from "../components/Brand";
import {
  PenLine, FolderOpen, Layers, UserSquare2, Building2, FileSignature, LogOut, ArrowRight, Lock, Home,
} from "lucide-react";

const CARDS = [
  { key: "ops", title: "Daily Log & Service Operations", admin: true, consultant: true, to: "/ops",
    icon: PenLine, testid: "card-service-ops",
    description: "Record your work and manage service records, follow-ups, evaluations, and invoices." },
  { key: "docs", title: "Company Documents", admin: true, consultant: true, to: "/documents",
    icon: FolderOpen, testid: "card-company-documents",
    description: "Access company agreements, policies, forms, and learning resources." },
  { key: "tiers", title: "Payment Tiers", admin: true, consultant: false, to: "/payment-tiers",
    icon: Layers, testid: "card-payment-tiers",
    description: "Manage client pricing, consultant compensation references, and financial planning documents." },
  { key: "profiles", title: "Consultant Profiles & Résumés", admin: true, consultant: false, to: "/profiles",
    icon: UserSquare2, testid: "card-consultant-profiles",
    description: "Maintain consultant qualifications and approved materials for staffing and proposals." },
  { key: "myprofile", title: "My Profile & Résumé", admin: false, consultant: true, to: "/my-profile",
    icon: UserSquare2, testid: "card-my-profile",
    description: "Keep your qualifications, bio, headshot, and résumé current for staffing and proposals." },
  { key: "clients", title: "Client Database", admin: true, consultant: false, to: "/clients",
    icon: Building2, testid: "card-client-database",
    description: "Manage prospective and current clients, contacts, and engagement documents." },
  { key: "proposals", title: "Proposal Generator", admin: true, consultant: false, to: "/proposals",
    icon: FileSignature, testid: "card-proposal-generator",
    description: "Draft branded proposals using approved services, client needs, pricing, and consultant qualifications." },
];

export default function HubHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const cards = CARDS.filter((c) => (isAdmin ? c.admin : c.consultant));

  return (
    <div className="min-h-screen bg-canvas" data-testid="hub-home">
      <header className="bg-navy text-white px-6 sm:px-10 py-5 flex items-center justify-between" data-testid="hub-header">
        <div>
          <Wordmark light />
          <span className="ml-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60 hidden sm:inline">Operations Hub</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-white/70 hidden sm:inline" data-testid="hub-user-name">{user?.name} · {isAdmin ? "Admin" : "Consultant"}</span>
          <button className="text-white/60 hover:text-white transition-colors" title="Sign out" data-testid="hub-logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}>
            <LogOut size={17} />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="fade-up max-w-2xl">
          <p className="eyebrow flex items-center gap-2"><Home size={13} /> Hub Home</p>
          <h1 className="section-title text-4xl sm:text-5xl mt-2">CKingEducation Operations Hub</h1>
          <p className="mt-4 text-[#57534E] leading-relaxed" data-testid="hub-subtitle">
            Your workspace for service delivery, company resources, and client partnerships.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12" data-testid="hub-card-grid">
          {cards.map((c, i) => (
            <div key={c.key} className={`card card-hover p-6 flex flex-col fade-up-${Math.min(i + 1, 3)}`} data-testid={c.testid}>
              <div className="w-11 h-11 rounded-xl bg-sand flex items-center justify-center text-terracotta mb-4">
                <c.icon size={21} />
              </div>
              <h2 className="font-serif font-bold text-lg text-navy leading-snug">{c.title}</h2>
              <p className="text-sm text-[#57534E] mt-2 leading-relaxed flex-1">{c.description}</p>
              <Link to={c.to} className="btn-outline mt-5 self-start !px-4 !py-2" data-testid={`${c.testid}-open`}>
                Open <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-12 text-xs text-[#78716C] flex items-center gap-1.5">
          <Lock size={11} /> Private internal system. Separate from the public CKingEducation website, member accounts, and Stripe.
        </p>
      </main>
    </div>
  );
}
